import React, { Fragment, useEffect } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import loadable from '@loadable/component';

import { updateCart, launchCartFlyout, closeCartFlyout } from '../../utility-actions';
import { showPopover } from '../../../page/components/header-footer/header-footer-actions';
import { Helpers } from '../../../core/src/helpers';
import ScrollEvent from '../../../shared/components/scroll-event';
import { withError } from '../../../shared/components/error-boundary';
import withWindowResize from '../../../ui/components/window-resize';
import AboveTheFoldOnlyServerRender from '../../../shared/components/above-the-fold-only-server-render';
import StickyCart from './sticky-cart';
import { trackCartQuantityIncrease } from '../../../metrics/track-params';

import '../../css/cart-preview.less';
import '../../css/new-cart-preview.less';
import '../../css/added-to-cart-flyout.less';
import '../../css/visid-cart-preview.less';
import { Typography } from '@hpi-sox/hpstellar-core';

const CartFlyOut = loadable(() => Helpers.retryFunc(() => import('./cart-flyout')));
const OldCartContent = loadable(() => Helpers.retryFunc(() => import('./cart-preview-content')));
const VisIDCartContent = loadable(() =>
    Helpers.retryFunc(() => import('./../visid-cart-preview/cart-preview-content')),
);
const VisIDEmptyCart = loadable(() => Helpers.retryFunc(() => import('./../visid-cart-preview/empty-cart-content')));

const MAX_PRODUCT_LIST_ITEMS = 3;
const MAX_DIGIT_PRODUCT_LIST_ITEMS = 4;
const MAX_CART_ICON_ITEMS = 3;
const POPOVER_KEY = 'cartPreviewClassic';

const EmptyCart = props => {
    const { variation, labels, onClick } = props;
    if (variation === 'digit') {
        return <VisIDEmptyCart {...props} />;
    }
    return (
        <Fragment>
            <div className="top_corner">&nbsp;</div>
            <a href="" className="hf_close_btn" onClick={onClick}></a>
            <div className="minicart_contents-empty minicart_contents">
                <Typography variant="titleM" tag="h3">
                    {labels.emptyCart}
                </Typography>
            </div>
        </Fragment>
    );
};

/**
 * Lazyload wrapper for cart content
 * @param {*} props
 * @returns
 */
const CartContentContainer = props => {
    const { variation } = props;
    return (
        <AboveTheFoldOnlyServerRender skip={true} placeholder={<div style={{ minHeight: '300px' }}></div>}>
            {variation === 'digit' ? <VisIDCartContent {...props} /> : <OldCartContent {...props} />}
        </AboveTheFoldOnlyServerRender>
    );
};

/**
 * Preload async components on mount
 * @returns
 */
const Preloader = ({ variation }) => {
    useEffect(() => {
        setTimeout(() => {
            if (variation === 'digit') {
                VisIDCartContent.preload();
            } else {
                OldCartContent.preload();
            }
            CartFlyOut.preload();
        }, 300);
    }, []);
    return null;
};

class CartPreview extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            recentlyAdded: [],
            previewSet: false,
            stickyCart: false,
            changingQty: false,
            lastKeyWasTab: false,
        };

        this.hoverTO;
        this.cartBtn;
        this.cartUrl;
        this.iCount;
        this.closeTimeout;
        this.handleQtyChange = this.handleQtyChange.bind(this);
        this.handleHover = this.handleHover.bind(this);
        this.handleMouseLeave = this.handleMouseLeave.bind(this);
        this.startCartTimeout = this.startCartTimeout.bind(this);
        this.hoverOff = this.hoverOff.bind(this);
        this.clearCloseTimeout = this.clearCloseTimeout.bind(this);
        this.handleFocus = this.handleFocus.bind(this);
        this.handleKeyDown = this.handleKeyDown.bind(this);
        this.handleMouseDown = this.handleMouseDown.bind(this);
        this.handleOnBlur = this.handleOnBlur.bind(this);
        this.containerRef = React.createRef();
    }

    componentWillUnmount() {
        clearTimeout(this.cartTimeout);
    }

    componentDidUpdate(prevProps) {
        let newItems = this.props.cartInfo.items;
        let oldItems = prevProps.cartInfo.items;

        if (prevProps.cartInfo.tQty != this.props.cartInfo.tQty) {
            this.updateCartIcon(this.props);
        }

        if (!newItems || !oldItems) {
            return;
        }

        if (newItems.length > oldItems.length) {
            const { addToCartResponse } = this.props.cartInfo.recentlyAdded || {};
            const { newOrderItemId_0, orderItemId = [] } = addToCartResponse || {};
            const oItemId = newOrderItemId_0 || orderItemId[0];
            this.setState({
                recentlyAdded: [newItems.filter(p => p.id === oItemId)[0]],
                stickyCart: true,
            });
            this.startCartTimeout();
        } else {
            for (let i = 0; i < Math.min(newItems.length, oldItems.length); i++) {
                if (newItems[i].amt.fg) continue;
                if (newItems[i].qty !== oldItems[i].qty) {
                    this.setState({
                        recentlyAdded: [newItems[i]],
                        stickyCart: true,
                    });
                    this.startCartTimeout();
                    break;
                }
            }
        }
    }

    startCartTimeout() {
        if (this.cartTimeout) clearTimeout(this.cartTimeout);
        this.cartTimeout = setTimeout(() => this.setState({ stickyCart: false }), 3000);
    }

    updateCartIcon(nextProps) {
        const currQty = nextProps.cartInfo.tQty;
        const displayPlus = currQty > MAX_CART_ICON_ITEMS;

        if (!this.iCount) {
            return;
        }

        if (currQty > 0) {
            this.iCount.classList.add('hf_cart_not_empty');
            this.iCount.innerText = Math.min(currQty, MAX_CART_ICON_ITEMS);
            this.iCount.innerText += displayPlus ? '+' : '';
        } else {
            this.iCount.classList.remove('hf_cart_not_empty');
            this.iCount.innerText = ' ';
        }
    }

    getTrackMiniCartQuantityChangeFunc = (cartItem, qty) => cartData => {
        try{
            this.props.trackMetrics(cartItem, qty, cartData)
        }catch(e){}
    }

    handleQtyChange = (qty, cartItem) => {
        this.setState({ changingQty: true });
        let _cItem = Object.assign({}, cartItem);

        _cItem.amt.nAmt = Helpers.convertPriceToNumber(_cItem.amt.nAmt);
        _cItem.amt.iPrice = Helpers.convertPriceToNumber(_cItem.amt.iPrice);
        _cItem.amt.uPrice = Helpers.convertPriceToNumber(_cItem.amt.uPrice);

        let trackEvent;
        try{
            trackEvent = this.getTrackMiniCartQuantityChangeFunc(_cItem, qty);
        }catch(e){}

        this.props.updateCart(_cItem, qty, trackEvent).then(() => {
            this.setState({ changingQty: false });
        });
    };

    handleHover() {
        const { launchPopover, localState, onHover } = this.props;
        if (this.closeTimeout) {
            this.clearCloseTimeout(this.closeTimeout);
            this.closeTimeout = null;
        }
        this.hoverTO = setTimeout(() => {
            this.setState({ previewSet: true });
            if (!localState) {
                launchPopover();
            }
            typeof onHover === 'function' && onHover();
        }, 50);
    }

    handleKeyDown(e) {
        if (e.key === 'Tab') {
            this.setState({ lastKeyWasTab: true });
        }
    }

    handleMouseDown() {
        this.setState({ lastKeyWasTab: false });
    }

    handleFocus() {
        if (this.state.lastKeyWasTab) {
            this.handleHover();
        }
    }

    handleOnBlur() {
        setTimeout(() => {
            const container = this.containerRef?.current;
            const active = document.activeElement;
            if (container && !container.contains(active)) {
                this.handleMouseLeave();
            }
        }, 0);
    }

    handleMouseLeave() {
        const { closePopover, variation } = this.props;
        if (this.hoverTO) {
            this.clearCloseTimeout(this.hoverTO);
            this.hoverTO = null;
        }
        //on mouse leave, close the popover after 1 second, child component can cancel this by calling clearCloseTimeout
        this.closeTimeout = setTimeout(() => {
            if (variation !== 'digit') return;
            closePopover();
            this.setState({ previewSet: false });
        }, 500);
    }

    handleCartBtnClick = () => {
        const { launchPopover, localState, device, history } = this.props;
        const { crtUrl, iCount } = this.props.cartInfo;
        setTimeout(() => {
            if (iCount > 0 || device === 'mobile') {
                if (crtUrl === `${process.env.BASENAME}/cart`) {
                    history.push(Helpers.decodeHtml(crtUrl));
                } else {
                    window.location = Helpers.decodeHtml(crtUrl);
                }
            } else if (!localState) {
                launchPopover();
            }
        }, 0);
    };

    hoverOff() {
        const { closePopover } = this.props;
        closePopover();
        this.setState({ previewSet: false });
        clearTimeout(this.hoverTO);
    }

    closeClick = e => {
        const { closePopover } = this.props;
        e.preventDefault();
        this.setState({ previewSet: false });
        closePopover();
    };

    clearCloseTimeout = () => {
        clearTimeout(this.closeTimeout);
    };

    stickyMobileCart() {
        let cartQty = this.props.cartInfo.tQty;
        const cartUrl = Helpers.decodeHtml(this.props.cartInfo.crtUrl);
        if (!cartQty) cartQty = 0;
        else if (cartQty > MAX_CART_ICON_ITEMS) cartQty = MAX_CART_ICON_ITEMS + '+';
        if (!this.props.stickyCart) {
            return null;
        } else {
            return (
                <ScrollEvent enableDelta="continuous">
                    {(attachRef, scrollState) => {
                        const show =
                            scrollState.isSticky &&
                            this.props.cartInfo.tQty > 0 &&
                            (this.state.stickyCart || scrollState.delta < 0);
                        return <StickyCart show={show} cartUrl={cartUrl} qty={cartQty} ref={attachRef} />;
                    }}
                </ScrollEvent>
            );
        }
    }

    render() {
        const {
            closeCartFlyout,
            cartInfo,
            updateCart,
            pageType,
            device,
            isNewVersion,
            children,
            showPreview,
            localState,
            enableCartFlyout = true,
            closePopover,
            variation,
        } = this.props;
        const { recentlyAdded, changingQty, previewSet } = this.state;
        const { addToCartFlyoutVisible, tQty } = cartInfo;
        const isMobile = device === 'mobile';
        let displayCount = tQty > MAX_CART_ICON_ITEMS ? `${MAX_CART_ICON_ITEMS}+` : tQty;
        const displayCartPreview = showPreview || (localState && previewSet);
        if (cartInfo.labels) {
            return (
                <Fragment>
                    <Preloader variation={variation} />
                    {children &&
                        children({
                            onHover: this.handleHover,
                            onClick: this.handleCartBtnClick,
                            count: tQty > 0 ? displayCount : '',
                            close: closePopover,
                            closeTimeout: this.closeTimeout,
                            onMouseLeave: this.handleMouseLeave,
                            onFocus: this.handleHover,
                            onBlur: this.handleOnBlur,
                            onkeydown: this.handleKeyDown,
                            onmousedown: this.handleMouseDown,
                            containerRef: this.containerRef,
                        })}
                    <AboveTheFoldOnlyServerRender skip={true} robots={false}>
                        {enableCartFlyout && (
                            <CartFlyOut
                                cartInfo={cartInfo}
                                recentlyAddedCartItem={recentlyAdded}
                                addToCartFlyoutVisible={addToCartFlyoutVisible}
                                isNewCartPreview={isNewVersion}
                                closeCartFlyout={closeCartFlyout}
                                updateCart={updateCart}
                                pageType={pageType}
                                showPreview={showPreview}
                            />
                        )}
                        {isMobile && this.stickyMobileCart()}
                        <div style={{ position: 'absolute', height: 0, width: 0 }}>
                            {displayCartPreview && !isMobile && cartInfo.iCount !== undefined && (
                                <div
                                    className={`minicart-container preview${displayCartPreview ? ' visible' : ''}${
                                        cartInfo.iCount === 0 ? ' empty-cart' : ''
                                    }${variation === 'digit' ? ' visid-css' : ''}`}
                                    onMouseLeave={this.hoverOff}
                                    onBlur={this.handleOnBlur}
                                    ref={this.containerRef}
                                    tabIndex={-1}
                                >
                                    <div className="shopping_container">
                                        {cartInfo.iCount === 0 ? (
                                            <EmptyCart
                                                labels={cartInfo.labels}
                                                onClick={this.closeClick}
                                                onMouseEnter={this.clearCloseTimeout}
                                                variation={variation}
                                            />
                                        ) : (
                                            <CartContentContainer
                                                variation={variation}
                                                cartInfo={cartInfo}
                                                showPreview={displayCartPreview}
                                                updateCart={updateCart}
                                                handleQtyChange={this.handleQtyChange}
                                                changingQty={changingQty}
                                                maxCartItems={
                                                    variation === 'digit'
                                                        ? MAX_DIGIT_PRODUCT_LIST_ITEMS
                                                        : MAX_PRODUCT_LIST_ITEMS
                                                }
                                                onClick={this.closeClick}
                                                onMouseEnter={this.clearCloseTimeout}
                                            />
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </AboveTheFoldOnlyServerRender>
                </Fragment>
            );
        } else {
            return this.stickyMobileCart();
        }
    }
}

const mapStateToProps = state => {
    const { popover } = state.headerFooter;
    return {
        cartInfo: state.cartInfo,
        testFlags: state.testFlags,
        showPreview: popover === POPOVER_KEY,
    };
};

const mapDispatchToProps = dispatch => ({
    updateCart: (cItem, qty, trackMetrics) => dispatch(updateCart(cItem, qty, trackMetrics)),
    launchCartFlyout: flyoutVersion => dispatch(launchCartFlyout(flyoutVersion)),
    closeCartFlyout: flyoutVersion => dispatch(closeCartFlyout(flyoutVersion)),
    closePopover: () => dispatch(showPopover(null)),
    launchPopover: () => dispatch(showPopover(POPOVER_KEY)),
    trackMetrics: (cItem, newQuantity) => {
        try{
            const { id, qty } = cItem || {};
            const quantityChange = newQuantity - qty;
            if(quantityChange > 0){
                dispatch(trackCartQuantityIncrease(id, quantityChange));
            }
        }catch(e){}
    }
});

export default connect(mapStateToProps, mapDispatchToProps)(withError(withWindowResize(withRouter(CartPreview))));
